"use client";

import { ArrowRight } from "lucide-react";
import { ImpactMetricCard } from "@/components/home/cards/ImpactMetricCard";
import { HomeSectionHeader } from "@/components/home/HomeSectionHeader";
import { DonateFloatingLeaves } from "@/components/donate/DonateFloatingLeaves";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { LinkButton } from "@/components/ui/LinkButton";
import { splitHomeImpactMetrics } from "@/content/home";
import type { HomeImpactContent, HomeImpactMetric, Stats } from "@/lib/api-types";
import { resolveHomeImpactMetrics } from "@/lib/site-content-fallbacks";

interface HomeImpactSectionProps {
  stats?: Stats;
  impact: HomeImpactContent;
}

export function HomeImpactSection({ stats, impact }: HomeImpactSectionProps) {
  const metrics: HomeImpactMetric[] = resolveHomeImpactMetrics(impact, stats);
  const { featured, supporting } = splitHomeImpactMetrics(metrics);

  return (
    <section
      id="impact"
      className="home-charity-section home-impact-showcase relative overflow-hidden bg-[var(--charity-ivory)] py-16 md:py-20"
    >
      <DonateFloatingLeaves />

      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6">
        <HomeSectionHeader
          eyebrow={impact.eyebrow}
          title={impact.title}
          subtitle={impact.subtitle}
          tone="light"
          align="left"
        />

        <div className="home-impact-showcase__panel rounded-3xl border border-[var(--charity-green)]/15 bg-[var(--charity-green)]/8 p-6 md:p-8 lg:p-10">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            <Reveal>
              <ImpactMetricCard metric={featured} variant="featured" />
            </Reveal>

            <Stagger className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1">
              {supporting.map((metric, index) => (
                <StaggerItem key={metric.id}>
                  <ImpactMetricCard
                    metric={metric}
                    index={index}
                    variant="compact"
                  />
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 flex justify-center md:justify-start">
              <LinkButton
                href={impact.ctaHref}
                variant="accent"
                size="lg"
                className="inline-flex items-center gap-2"
              >
                {impact.cta}
                <ArrowRight size={18} aria-hidden />
              </LinkButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
